import React, { useContext, useEffect, useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    MenuItem,
    TextField,
    useTheme,
} from '@mui/material';
import { AuthContext } from '../../../context/auth.context';
import moment from "moment";
import { addGuestPayment, getReportData, getUnallocatedAmount } from '../../../service/network';

// Styled Components
const StyledTextField = ({ label, align = 'left', ...props }) => {
    const theme = useTheme();
    const inputBoxStyle = {
        "& .MuiOutlinedInput-root": {
            borderRadius: theme.borderRadius?.medium || 8,
            backgroundColor: theme.palette?.lightgray?.main || "#F3F4F6",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
            "& fieldset": {
                borderColor: theme.palette?.gray?.main || "#D1D5DB",
            },
            "&:hover fieldset": {
                borderColor: theme.palette.primary.main,
            },
            "&.Mui-focused": {
                backgroundColor: theme.palette?.light?.main || "#f8f8ff",
                "& fieldset": {
                    borderColor: theme.palette.primary.main,
                },
            },
            "& input": {
                textAlign: align,
            },
        },
    };

    return (
        <Box>
            {label && (
                <Typography variant="boddyText1" sx={{ mb: 0.5 }}>
                    {label}
                </Typography>
            )}
            <TextField size="small" fullWidth sx={inputBoxStyle} {...props} />
        </Box>
    );
};

const PaymentList = ({ reservation_id, records, refresh, unallocated = 0 }) => {
    const [open, setOpen] = useState(false);
    const [paymentType, setPaymentType] = useState('Receive Payment');
    const [mode, setMode] = useState('UPI');
    const [modes, setModes] = useState([]);
    const [amount, setAmount] = useState('');
    const { selLocation } = useContext(AuthContext);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let params = {
            doctype: 'Mode of Payment',
            fields: JSON.stringify(["name"]),
            filters: JSON.stringify({}),
        }
        getReportData(params).then((resp) => {
            setModes(resp.data.message.values);
        }).catch((err) => {
            console.log(err);
        })
    }, [])

    const handleSubmitPayment = async () => {
        try {
            setLoading(true);
            await addGuestPayment({
                "reservation": reservation_id,
                "amount": parseFloat(amount),
                "mode_of_payment": mode,
                "type": paymentType,
                "remarks": "",
                "company": selLocation[0]
            });
            refresh(); // This will refresh the parent component which will update unallocated
            setOpen(false);
            setAmount(''); // Reset amount after successful payment
        } catch (err) {
            console.log(err);
        } finally {
            setLoading(false);
        }
    }

    return (
        <>
            <Box
                sx={{
                    backgroundColor: 'light.main',
                    borderRadius: 2,
                    boxShadow: '0px 4px 12px rgba(74, 76, 78, 0.25)',
                    p: 2,
                    height: 300,
                    maxHeight: 500,
                    display: 'flex',
                    flexDirection: 'column'
                }}
            >
                {/* Header */}
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="SubTitleBold">Payment List</Typography>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <Typography variant="buttonL" minWidth={180}>Un-allocated Amount:</Typography>
                        <Box
                            sx={{
                                backgroundColor: 'green.main',
                                color: 'light.main',
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            ₹ {unallocated}
                        </Box>
                    </Box>

                    <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="contained"
                            onClick={() => setOpen(true)}
                            sx={{
                                backgroundColor: 'primary.main',
                                color: 'light.main',
                                textTransform: 'none',
                                px: 3,
                                borderRadius: 2
                            }}
                        >
                            Payment
                        </Button>
                    </Box>
                </Box>

                {/* Scrollable Table */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                    <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {["Date & Time", "Mode of Payment", "Amount"].map((text, index) => (
                                        <TableCell
                                            key={index}
                                            sx={{
                                                backgroundColor: 'secondary.main',
                                                color: '#333333',
                                                fontFamily: 'Nunito Sans',
                                                fontSize: '14px',
                                                fontWeight: 700,
                                            }}
                                        >
                                            {text}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {records && records.length > 0 ? (
                                    records.map((payment, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>{moment(payment[1]).format("DD-MM-YYYY HH:mm:ss")}</TableCell>
                                            <TableCell>{payment[4]}</TableCell>
                                            <TableCell
                                                sx={{
                                                    color: payment[3] === "Receive" ? "#00A86B" : "#FF3B30",
                                                    fontWeight: "bold",
                                                    textAlign: "right"
                                                }}
                                            >
                                                {payment[3] === "Receive" ? `₹${payment[5]}` : `-₹${payment[5]}`}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3} align="center">
                                            No Payments found
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            </Box>

            {/* Payment Modal */}
            <Dialog open={open} onClose={() => {
                setOpen(false);
                setAmount(''); // Reset amount when closing dialog
            }} maxWidth="xs" fullWidth>
                <DialogTitle>
                    <Typography variant="SubTitleBold" color='primary.main'>Payment</Typography>
                </DialogTitle>

                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box display="flex" alignItems="center" justifyContent="flex-end">
                        <Typography variant="buttonL" minWidth="180px">Un-allocated Amount :</Typography>
                        <Box
                            sx={{
                                backgroundColor: 'green.main',
                                color: 'light.main',
                                px: 2,
                                py: 0.5,
                                borderRadius: 2,
                                fontWeight: 'bold'
                            }}
                        >
                            ₹ {unallocated}
                        </Box>
                    </Box>

                    <StyledTextField
                        label="Transaction Type"
                        select
                        value={paymentType}
                        onChange={(e) => setPaymentType(e.target.value)}
                        SelectProps={{
                            displayEmpty: true,
                            MenuProps: {
                                PaperProps: {
                                    sx: {
                                        '& .MuiMenuItem-root:hover': {
                                            backgroundColor: '#E1EFFF',
                                        },
                                    },
                                },
                            },
                        }}
                    >
                        <MenuItem value="" disabled>
                            Select Transaction Type
                        </MenuItem>
                        <MenuItem value="Receive">Receive Payment</MenuItem>
                        <MenuItem value="Pay">Refund to Guest</MenuItem>
                    </StyledTextField>

                    <Box display="flex" gap={2}>
                        <StyledTextField
                            label="Mode of Payment"
                            select
                            value={mode}
                            onChange={(e) => setMode(e.target.value)}
                            SelectProps={{
                                displayEmpty: true,
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root:hover': {
                                                backgroundColor: '#E1EFFF',
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <MenuItem value="" disabled>
                                Select Mode of Payment
                            </MenuItem>
                            {modes.map((ele, ind) => (
                                <MenuItem key={ind} value={ele[0]}>
                                    {ele[0]}
                                </MenuItem>
                            ))}
                        </StyledTextField>

                        <StyledTextField
                            label="Amount"
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            align="right"
                            placeholder="Enter Amount"
                        />
                    </Box>

                    <Box display="flex" justifyContent="flex-end" mt={2}>
                        <Button
                            disabled={loading || !amount || parseFloat(amount) <= 0}
                            onClick={handleSubmitPayment}
                            variant="contained"
                            sx={{
                                backgroundColor: paymentType === "Pay" ? 'error.main' : 'green.main',
                                color: 'light.main',
                                textTransform: 'none',
                                px: 4,
                                borderRadius: 2,
                                '&:hover': {
                                    backgroundColor: paymentType === "Pay" ? 'error.dark' : 'green.dark'
                                }
                            }}
                        >
                            {loading ? 'Processing...' : 'Submit'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default PaymentList;